import React, { ReactElement, useEffect, useState } from 'react';
import { ContentContainer } from '../component/containers';

import Box from '@mui/material/Box/Box';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  Caption,
  CellStatus,
  IconButton,
  StyledTextField,
  SubTitle,
  SubTitle2
} from '../component/atoms';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTheme } from '@mui/material';
import styled from 'styled-components';

import { useErrors, useLoading, useService } from '../hooks';
import useCoreState from '../hooks/useCoreState';
import { CoreActionTypes } from '../context';
import {
  entityUpdatedResult,
  errorMessage,
  fetchAll,
  reasonCode,
  ReqError,
  responseErrorHelper,
  scriptOP
} from '../network';
import { Company, LoadType, loadTypeFrom, Project, projectFrom, rw, Truck } from '../types';
import { decontructPackedId, getProvince } from '../util';
import { lightGrey } from '../theme';

const Header = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

/** A company row plus the trucks that would be hidden along with it. */
export type CompanyDisplay = Company & { truckCount: number; splIds: string };

const columns = (
  toggleCompany: (row: CompanyDisplay) => void,
  hasPermission: boolean
): GridColDef[] => {
  return [
    { field: 'name', headerName: 'Company Name', flex: 1, minWidth: 200, hideable: false },
    {
      // Truck IDs use their own abbreviations (Robinson Adisoke -> Rob-Adisoke, and
      // Maurice Yelle -> Burns), so the company name alone is not enough to tell which
      // trucks a company covers. Showing the prefixes makes that link explicit.
      field: 'splIds',
      headerName: 'Truck IDs',
      flex: 1,
      minWidth: 170,
      hideable: false
    },
    {
      field: 'truckCount',
      headerName: 'Trucks',
      width: 100,
      hideable: false,
      type: 'number'
    },
    {
      field: 'isActive',
      headerName: 'Status',
      filterable: false,
      hideable: false,
      width: 120,
      renderCell: (v) => {
        const data = v.row as CompanyDisplay;
        return <CellStatus isActive={data.isActive} />;
      }
    },
    {
      field: 'Actions',
      headerName: '',
      width: 90,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (v) => {
        const data = v.row as CompanyDisplay;
        return (
          <IconButton
            disabled={!hasPermission}
            onClick={() => toggleCompany(data)}
            height="40px"
            width="40px"
            label={data.isActive ? 'Disable company' : 'Activate company'}>
            {data.isActive ? (
              <DoDisturbIcon
                sx={{ height: 28, width: 28, color: hasPermission ? '#BD1414' : lightGrey }}
              />
            ) : (
              <CheckCircleOutlineIcon
                sx={{ height: 28, width: 28, color: hasPermission ? '#366D36' : lightGrey }}
              />
            )}
          </IconButton>
        );
      }
    }
  ];
};

export const Companies = (): ReactElement => {
  const { coreState, updateCoreState } = useCoreState();
  const { service } = useService();
  const { setLoading } = useLoading();
  const { setError } = useErrors();
  const theme = useTheme();

  const [search, setSearch] = useState('');
  const [showDisabled, setShowDisabled] = useState(true);

  const hasPermission = coreState.permission.company >= rw;

  const initPageLoad = async () => {
    setLoading(true);
    try {
      const allData = await fetchAll(service);
      const error = responseErrorHelper(allData);

      if (error && error.reason_code === reasonCode.sessionTimeout) {
        setError({ networkError: errorMessage.sessionTimeout });
        return;
      }

      if (allData && 'users' in allData) {
        const companies: Company[] = allData.truckingCompanies.map((c): Company => {
          return {
            version: c.version,
            entityId: c.entityId,
            name: c.data.name,
            isActive: c.data.isActive !== false,
            contactName: c.data.contactName ?? '',
            contactphone: c.data.contactphone ?? ''
          };
        });

        const trucks: Truck[] = allData.trucks.map((t): Truck => {
          return {
            companyId: t.data.companyId,
            driver: { ...t.data.driver },
            scheduleDate: t.data.scheduleDate,
            splId: t.data.splId,
            plate: t.data.plate,
            capacity: t.data.capacity,
            entityId: t.entityId,
            version: t.version,
            driverId: t.data.driverId,
            province: getProvince(t.data.province),
            isAssigned: t.data.isAssigned,
            projectId: t.data.projectId,
            truckNumber: t.data.truckNumber,
            note: t.data.note ? t.data.note : '',
            noteImage: t.data.noteImage ? t.data.noteImage : ''
          };
        });

        const projects: Project[] = allData.projects.map((p) => projectFrom(p));
        const loadTypes: LoadType[] = allData.loadTypes.map(loadTypeFrom);

        updateCoreState({
          type: CoreActionTypes.SET_INIT_LOAD,
          payload: { ...coreState, companies, trucks, projects, loadTypes }
        });
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'reason_code' in error) {
        if (error.reason_code === reasonCode.sessionTimeout) {
          setError({ networkError: errorMessage.sessionTimeout });
          return;
        }
        if (error.reason_code === reasonCode.packactIdError) {
          const id = decontructPackedId(error);
          if (id !== 0) {
            service.setPacketID(id);
            initPageLoad();
            return;
          }
        }
        setError({ networkError: errorMessage.somethingWrong });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initPageLoad();
  }, []);

  async function toggleCompany(row: CompanyDisplay, isRetry = false) {
    try {
      const res = await service.post(scriptOP.updateEntity, {
        dataJson: {
          name: row.name,
          isActive: !row.isActive
        },
        entityType: 'TruckingCompany',
        acl: { other: 2 },
        entityId: row.entityId,
        version: row.version
      });

      // take the version the server just wrote rather than assuming version + 1,
      // otherwise one partial failure leaves the cached version stale for good
      const updated = entityUpdatedResult<Company>(res);

      if (!updated || typeof updated.version !== 'number') {
        setError({ pageError: errorMessage.somethingWrong });
        return;
      }

      updateCoreState({
        type: CoreActionTypes.SET_COMPANIES,
        payload: {
          ...coreState,
          companies: coreState.companies.map((c) =>
            c.entityId === row.entityId
              ? { ...c, isActive: !row.isActive, version: updated.version }
              : c
          )
        }
      });
    } catch (e) {
      const err = e as ReqError;

      if (err && err.reason_code === reasonCode.sessionTimeout) {
        setError({ networkError: errorMessage.sessionTimeout });
        return;
      }

      // the packet counter drifts whenever requests overlap; resync and try once more,
      // the same recovery the other pages do. Without this every later toggle fails too.
      if (err && err.reason_code === reasonCode.packactIdError && !isRetry) {
        const id = decontructPackedId(err);
        if (id !== 0) {
          service.setPacketID(id);
          await toggleCompany(row, true);
          return;
        }
      }

      // stale version: the company changed since this page loaded
      if (err && err.reason_code === reasonCode.badversion) {
        setError({ pageError: 'This company was changed elsewhere. Reloading the list.' });
        initPageLoad();
        return;
      }

      setError({ networkError: errorMessage.somethingWrong });
    }
  }

  // Every company stays in coreState so trucks and load history can still resolve a
  // company name. Only this list — what the grid shows — is filtered.
  const truckCountByCompany: Record<string, number> = {};
  const splIdsByCompany: Record<string, string[]> = {};

  coreState.trucks.forEach((t) => {
    if (!t.companyId) return;
    truckCountByCompany[t.companyId] = (truckCountByCompany[t.companyId] ?? 0) + 1;
    if (!splIdsByCompany[t.companyId]) splIdsByCompany[t.companyId] = [];
    if (t.splId && !splIdsByCompany[t.companyId].includes(t.splId)) {
      splIdsByCompany[t.companyId].push(t.splId);
    }
  });

  const term = search.trim().toLowerCase();
  const rows: CompanyDisplay[] = coreState.companies
    .filter((c) => showDisabled || c.isActive)
    .map((c) => ({
      ...c,
      truckCount: truckCountByCompany[c.entityId] ?? 0,
      splIds: (splIdsByCompany[c.entityId] ?? []).sort().join(', ')
    }))
    // search matches the truck ID too, so looking up "Rob-Adisoke" finds "Robinson Adisoke"
    .filter((c) =>
      term === ''
        ? true
        : c.name.toLowerCase().includes(term) || c.splIds.toLowerCase().includes(term)
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const disabledCount = coreState.companies.filter((c) => !c.isActive).length;
  const hiddenTruckCount = coreState.companies
    .filter((c) => !c.isActive)
    .reduce((sum, c) => sum + (truckCountByCompany[c.entityId] ?? 0), 0);

  const truckWord = hiddenTruckCount === 1 ? 'truck' : 'trucks';
  const summaryText =
    disabledCount > 0
      ? `${disabledCount} of ${coreState.companies.length} companies disabled, hiding ${hiddenTruckCount} ${truckWord}.`
      : `${coreState.companies.length} companies, none disabled.`;

  return (
    <ContentContainer header title="Trucking Companies">
      <Caption>
        NOTE: Disabling a company hides its trucks from the truck lists and from the operators&apos;
        app. Nothing is deleted &mdash; past loads and exports are unaffected.
      </Caption>
      <SubTitle2 style={{ margin: 0, marginTop: theme.spacing(3), marginBottom: theme.spacing(1) }}>
        {summaryText}
      </SubTitle2>
      <Box
        sx={{
          height: '70%',
          width: '100%',
          minHeight: '460px',
          backgroundColor: 'white',
          padding: 2,
          borderRadius: '15px',
          boxShadow: theme.shadows[1]
        }}>
        <Header>
          <SubTitle>Companies</SubTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <StyledTextField
              label="Search company or truck ID"
              size="small"
              variant="filled"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: 260 }}
            />
            <Caption
              style={{ cursor: 'pointer', color: theme.palette.secondary.main, margin: 0 }}
              onClick={() => setShowDisabled(!showDisabled)}>
              {showDisabled ? 'Hide disabled' : `Show disabled (${disabledCount})`}
            </Caption>
          </div>
        </Header>
        <Box sx={{ height: '100%', width: '100%', minHeight: '390px' }}>
          <DataGrid
            rows={rows}
            columns={columns(toggleCompany, hasPermission)}
            getRowId={(r) => r.entityId}
            disableSelectionOnClick
            rowsPerPageOptions={[25, 50, 100]}
            initialState={{ pagination: { pageSize: 25 } }}
          />
        </Box>
      </Box>
    </ContentContainer>
  );
};
